import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Camera, UploadSimple, Sparkle, ArrowRight, Trash, ArrowsClockwise,
  LockSimple, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PlanBanner from "@/components/PlanBanner";
import InstallHint from "@/components/InstallHint";
import { usePlanUsage } from "@/lib/plan";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";

const CAM_PRIME_KEY = "tally:cam-primed";

const MODELS = [
  { value: "gemini-3-flash-preview", label: "Gemini 3 Flash · fast" },
  { value: "gpt-4o", label: "GPT-4o Vision · accurate" },
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const compressImage = (dataUrl, maxDim = 1280, quality = 0.85) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.src = dataUrl;
  });

const fmtCarbs = (n) => `${(Math.round(n * 10) / 10)}g`;

const dayLabel = (dateStr, createdAt) => {
  const today = new Date().toISOString().slice(0, 10);
  if (dateStr === today) {
    return new Date(createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function SnapMeal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { usage, refresh: refreshUsage } = usePlanUsage();
  const isAdvanced = usage?.plan?.tier === "advanced";

  const fileRef = useRef(null);
  const [showCamPrompt, setShowCamPrompt] = useState(false);

  const openPicker = () => {
    const primed = typeof window !== "undefined" && localStorage.getItem(CAM_PRIME_KEY) === "1";
    if (!primed) {
      setShowCamPrompt(true);
      return;
    }
    fileRef.current?.click();
  };

  const handleCamAllow = () => {
    try { localStorage.setItem(CAM_PRIME_KEY, "1"); } catch {}
    setShowCamPrompt(false);
    // open the picker on the next tick so the dialog closes cleanly
    setTimeout(() => fileRef.current?.click(), 100);
  };

  const [model, setModel] = useState(user?.preferred_model || "gemini-3-flash-preview");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fullBase64, setFullBase64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const [recent, setRecent] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPortion, setNewPortion] = useState("");
  const [newCarbs, setNewCarbs] = useState("");

  const totalCarbs = useMemo(
    () => (result ? result.ingredients.reduce((s, i) => s + (i.carbs_g || 0), 0) : 0),
    [result]
  );

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await api.get("/meals/recent?limit=5");
      setRecent(data.meals || []);
    } catch {}
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const onFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Use a JPG, PNG, or WEBP image.");
      return;
    }
    const original = await fileToBase64(file);
    const compressed = await compressImage(original);
    setPreviewUrl(compressed);
    setFullBase64(compressed);
    setResult(null);
    setNotes("");
  };

  const handleAnalyze = async () => {
    if (!fullBase64) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/meals/analyze-photo", {
        image_base64: fullBase64,
        model,
      });
      setResult(data);
      refreshUsage();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || "Analysis failed";
      if (status === 402) {
        toast.error(detail, {
          action: { label: "Upgrade", onClick: () => navigate("/profile?focus=upgrade") },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEditIngredient = (idx, field, value) => {
    setResult((r) => {
      const next = {
        ...r,
        ingredients: r.ingredients.map((i, k) =>
          k === idx ? { ...i, [field]: field === "carbs_g" ? Number(value) : value } : i
        ),
      };
      next.total_carbs_g = next.ingredients.reduce((s, i) => s + (i.carbs_g || 0), 0);
      return next;
    });
  };
  const handleRemoveIngredient = (idx) =>
    setResult((r) => {
      const ings = r.ingredients.filter((_, k) => k !== idx);
      return { ...r, ingredients: ings, total_carbs_g: ings.reduce((s, i) => s + (i.carbs_g || 0), 0) };
    });
  const handleAddIngredient = () => {
    setNewName("");
    setNewPortion("");
    setNewCarbs("");
    setAddOpen(true);
  };

  const confirmAddIngredient = () => {
    const name = newName.trim();
    const carbs = parseFloat(newCarbs);
    if (!name) {
      toast.error("Please add a name");
      return;
    }
    if (Number.isNaN(carbs)) {
      toast.error("Please enter carbs in grams");
      return;
    }
    setResult((r) => ({
      ...r,
      ingredients: [...r.ingredients, { name, portion: newPortion.trim(), carbs_g: carbs }],
    }));
    setAddOpen(false);
  };

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post("/meals", {
        ingredients: result.ingredients,
        total_carbs_g: totalCarbs,
        notes,
      });
      toast.success("Scan saved");
      // reset for the next scan
      setPreviewUrl(null);
      setFullBase64(null);
      setResult(null);
      setNotes("");
      loadRecent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save scan");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPreviewUrl(null);
    setFullBase64(null);
    setResult(null);
    setNotes("");
  };

  const deleteRecent = async (mealId) => {
    try {
      await api.delete(`/meals/${mealId}`);
      loadRecent();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="px-5 pt-4 pb-2">
      {/* Greeting */}
      <span className="label-eyebrow">Tally</span>
      <h1 className="font-serif-display text-3xl tracking-tight text-[#1A2421] mt-1">
        Hello, <em className="text-[#2B422F]">{user?.name || "friend"}</em>
      </h1>
      <p className="text-sm text-[#5C6B62] mt-2">
        Take a picture of your plate — we&apos;ll tell you the carbs.
      </p>

      <PlanBanner usage={usage} />
      <InstallHint />

      {/* Upload / preview */}
      <div className="card-organic p-5 mt-5" data-testid="upload-card">
        {!previewUrl ? (
          <div
            onClick={openPicker}
            className="rounded-3xl border-2 border-dashed border-[#E6E3D8] bg-[#F9F8F6] hover:bg-[#F0EFE9] cursor-pointer transition-colors h-56 flex flex-col items-center justify-center text-center px-5"
            data-testid="upload-photo-zone"
          >
            <UploadSimple size={28} weight="regular" className="text-[#5C6B62]" />
            <div className="font-serif-display text-xl text-[#1A2421] mt-3">Tap to take a photo</div>
            <div className="text-xs text-[#5C6B62] mt-1">JPG / PNG / WEBP</div>
          </div>
        ) : (
          <div className="relative rounded-3xl overflow-hidden border border-[#E6E3D8] aspect-[4/3] bg-black/5" data-testid="photo-preview">
            <img src={previewUrl} alt="Meal" className="w-full h-full object-cover" />
            {analyzing && <div className="scanline" />}
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="photo-file-input"
        />

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button onClick={openPicker} className="btn-outline flex items-center justify-center gap-2" data-testid="choose-photo-button">
            <Camera size={16} /> {previewUrl ? "Change" : "Choose"}
          </button>
          <button
            onClick={handleAnalyze}
            disabled={!fullBase64 || analyzing}
            className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="analyze-button"
          >
            {analyzing ? <ArrowsClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
            {analyzing ? "Analyzing…" : "Analyze with AI"}
          </button>
        </div>

        <div className="mt-5">
          <label className="label-eyebrow block mb-2">AI Model</label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="bg-[#F0EFE9] border-none rounded-xl h-12" data-testid="ai-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODELS.map((o) => {
                const isGpt = o.value === "gpt-4o";
                const locked = isGpt && !isAdvanced;
                return (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    disabled={locked}
                    data-testid={`ai-model-option-${o.value}`}
                  >
                    <span className="flex items-center gap-2">
                      {o.label}
                      {locked && <LockSimple size={12} weight="bold" className="text-[#C76E4B]" />}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {!isAdvanced && (
            <p className="text-[11px] text-[#5C6B62] mt-1.5">GPT-4o requires Advanced.</p>
          )}
        </div>
      </div>

      {/* Results */}
      {(result || analyzing) && (
        <div className="card-organic p-5 mt-5" data-testid="results-card">
          <div className="flex items-center justify-between">
            <span className="label-eyebrow">Breakdown</span>
            {result && <span className="text-xs text-[#5C6B62]">Confidence: <span className="text-[#1A2421] font-medium">{result.confidence}</span></span>}
          </div>

          {analyzing && (
            <div className="flex flex-col items-center justify-center text-center py-10">
              <ArrowsClockwise size={24} className="animate-spin text-[#2B422F]" />
              <p className="font-serif-display text-lg text-[#1A2421] mt-3">Reading the plate…</p>
            </div>
          )}

          {result && (
            <>
              <div className="mt-4 space-y-2" data-testid="ingredient-list">
                {result.ingredients.map((ing, idx) => (
                  <div key={idx} className="rounded-2xl bg-[#F9F8F6] border border-[#E6E3D8] p-3 flex items-center gap-2" data-testid={`ingredient-row-${idx}`}>
                    <input
                      className="input-organic !py-1.5 !px-2 flex-1 !bg-transparent"
                      value={ing.name}
                      onChange={(e) => handleEditIngredient(idx, "name", e.target.value)}
                      data-testid={`ingredient-name-${idx}`}
                    />
                    <input
                      className="input-organic !py-1.5 !px-2 w-20 !bg-transparent text-xs"
                      value={ing.portion}
                      onChange={(e) => handleEditIngredient(idx, "portion", e.target.value)}
                      placeholder="portion"
                      data-testid={`ingredient-portion-${idx}`}
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        step="0.1"
                        className="input-organic !py-1.5 !px-2 w-14 !bg-transparent text-right"
                        value={ing.carbs_g}
                        onChange={(e) => handleEditIngredient(idx, "carbs_g", e.target.value)}
                        data-testid={`ingredient-carbs-${idx}`}
                      />
                      <span className="text-xs text-[#5C6B62]">g</span>
                    </div>
                    <button onClick={() => handleRemoveIngredient(idx)} className="text-[#5C6B62] hover:text-[#C76E4B]" data-testid={`ingredient-remove-${idx}`} aria-label="Remove ingredient">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddIngredient}
                  className="w-full mt-2 rounded-2xl border-2 border-dashed border-[#E6E3D8] hover:border-[#2B422F] hover:bg-[#F0EFE9] py-3 text-sm font-medium text-[#1A2421] flex items-center justify-center gap-1.5 transition-colors"
                  data-testid="add-ingredient-button"
                >
                  <span className="text-base leading-none">+</span> Add an item the AI missed
                </button>
              </div>

              <div className="mt-4">
                <label className="label-eyebrow block mb-2">Notes (optional)</label>
                <textarea
                  className="input-organic min-h-[60px]"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything to remember about this meal…"
                  data-testid="notes-input"
                />
              </div>

              <div className="mt-5 border-t border-[#E6E3D8] pt-5">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="label-eyebrow">Total carbs in this meal</div>
                    <div className="font-serif-display text-5xl text-[#2B422F] tracking-tighter mt-1" data-testid="result-total-carbs">
                      {fmtCarbs(totalCarbs)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl bg-[#F0EFE9] px-3.5 py-2.5 text-[11px] leading-relaxed text-[#5C6B62] flex gap-2" data-testid="safety-reminder">
                  <span className="text-[#C76E4B] shrink-0">⚠️</span>
                  <span>This is an AI estimate — double-check with your care team before adjusting medication.</span>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button onClick={handleReset} className="btn-outline flex items-center justify-center gap-2" data-testid="reset-button">
                    <ArrowCounterClockwise size={14} /> New scan
                  </button>
                  <button onClick={handleSave} disabled={saving} className="btn-accent flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-meal-button">
                    {saving ? "Saving…" : "Save scan"} <ArrowRight size={14} weight="bold" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Recent scans */}
      {recent.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <span className="label-eyebrow">Recent scans</span>
            <Link to="/history" className="text-xs text-[#1A2421] hover:text-[#2B422F]" data-testid="see-all-scans">See all →</Link>
          </div>
          <div className="mt-3 space-y-2.5" data-testid="recent-scans-list">
            {recent.map((m) => (
              <div key={m.meal_id} className="rounded-2xl bg-white border border-[#E6E3D8] p-3 flex items-center justify-between gap-3" data-testid={`recent-scan-${m.meal_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-[0.12em] text-[#5C6B62]">{dayLabel(m.date, m.created_at)}</div>
                  <div className="mt-0.5 text-xs text-[#1A2421] truncate">
                    {m.ingredients.slice(0, 4).map((i) => i.name).join(", ") || "Meal"}
                  </div>
                </div>
                <div className="font-serif-display text-xl text-[#2B422F] shrink-0">{fmtCarbs(m.total_carbs_g)}</div>
                <button onClick={() => deleteRecent(m.meal_id)} className="text-[#5C6B62] hover:text-[#C76E4B] shrink-0" data-testid={`delete-recent-${m.meal_id}`} aria-label="Delete scan">
                  <Trash size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add-ingredient quick dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="add-ingredient-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#1A2421]">Add an item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="label-eyebrow block mb-1.5">What is it?</label>
              <input
                autoFocus
                className="input-organic"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Garlic bread"
                data-testid="add-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">Portion</label>
                <input
                  className="input-organic"
                  value={newPortion}
                  onChange={(e) => setNewPortion(e.target.value)}
                  placeholder="1 slice"
                  data-testid="add-portion-input"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Carbs (g)</label>
                <input
                  className="input-organic"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={newCarbs}
                  onChange={(e) => setNewCarbs(e.target.value)}
                  placeholder="20"
                  data-testid="add-carbs-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <DialogClose asChild>
              <button className="btn-outline" data-testid="add-cancel">Cancel</button>
            </DialogClose>
            <button onClick={confirmAddIngredient} className="btn-primary" data-testid="add-confirm">
              Add to meal
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera permission priming dialog */}
      <AlertDialog open={showCamPrompt} onOpenChange={setShowCamPrompt}>
        <AlertDialogContent className="max-w-sm rounded-2xl" data-testid="camera-permission-dialog">
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-[#2B422F] flex items-center justify-center mb-2">
              <Camera size={22} weight="regular" className="text-[#E6D4C4]" />
            </div>
            <AlertDialogTitle className="font-serif-display text-2xl text-[#1A2421] text-center">
              Tally would like to see your meal
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B62] text-sm leading-relaxed text-center">
              We use your camera to identify the food on your plate. Photos are sent to our AI just long enough to count the carbs — we don&apos;t keep them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:gap-2">
            <AlertDialogAction
              onClick={handleCamAllow}
              className="w-full bg-[#2B422F] hover:bg-[#1E3021] text-white"
              data-testid="camera-allow"
            >
              Continue
            </AlertDialogAction>
            <AlertDialogCancel className="w-full mt-0" data-testid="camera-deny">
              Not now
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
